import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useState,
} from 'react'

import { ImageProps } from '~/components/UI/Image'

import { Media } from '~/data/serialize-media'

type ProductZoomProviderValueProps = Partial<{
  isZoom: boolean
  hasZoom: boolean
  closeZoom: () => void
  openZoom: () => void
  imageIndex: number
  incrementIndex: (images: ImageProps[]) => void
  decrementIndex: (images: ImageProps[]) => void
  resetIndex: () => void
  images: ImageProps[]
  setImageIndex: Dispatch<number>
  setImages: Dispatch<ImageProps[]>
  setProductHeaderImages: Dispatch<ImageProps[]>
}>

type ProductZoomProviderProps = {
  medias: Media[]
} & PropsWithChildren

export const Context = createContext<ProductZoomProviderValueProps>({})

export function useProductZoomContext() {
  return useContext(Context)
}

export default function ProductZoomProvider({
  children,
  medias,
}: ProductZoomProviderProps) {
  const [imageIndex, setImageIndex] = useState(0)
  const [isZoom, setIsZoomed] = useState(false)

  const images = medias
    ?.filter((media) => !media.video)
    ?.map((media) => media.image)
    .filter(Boolean)

  const incrementIndex = (images: ImageProps[]) => {
    if (!images) return

    setImageIndex((lastIndex) => {
      const nextIndex = lastIndex + 1
      const isLastImage = nextIndex === images.length

      return isLastImage ? 0 : nextIndex
    })
  }

  const decrementIndex = (images: ImageProps[]) => {
    if (!images) return

    setImageIndex((lastIndex) => {
      const nextIndex = lastIndex - 1
      const isFirstImage = nextIndex === -1

      return isFirstImage ? images.length - 1 : nextIndex
    })
  }

  const resetIndex = () => {
    setImageIndex(0)
  }

  const providerValue: ProductZoomProviderValueProps = {
    isZoom,
    closeZoom: () => {
      if (images?.length > 0) {
        setIsZoomed(false)
      }
    },
    openZoom: () => {
      if (images?.length > 0) {
        setIsZoomed(true)
      }
    },
    hasZoom: images?.length > 0,
    imageIndex,
    images,
    incrementIndex,
    decrementIndex,
    resetIndex,
    setImageIndex,
  }

  return <Context.Provider value={providerValue}>{children}</Context.Provider>
}
